/**
 * SHA-256 over raw bytes, hex with 0x prefix (matches encrypt-time dataToEncryptHash).
 */
export async function sha256HexOfBytes(data: Uint8Array): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', data.buffer as ArrayBuffer)
  const bytes = new Uint8Array(hash)
  return (
    '0x' +
    Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  )
}

export function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

function normalizeBytes32Hex(h: string): string {
  const raw = h.startsWith('0x') ? h.slice(2) : h
  const lower = raw.toLowerCase()
  if (!/^[0-9a-f]{64}$/.test(lower)) {
    throw new Error('Invalid encryptionHash: expected 32-byte hex')
  }
  return '0x' + lower
}

/**
 * Throws if raw plaintext bytes do not match on-chain encryptionHash (bytes32).
 */
export async function assertBytesMatchEncryptionHash(
  plainBytes: Uint8Array,
  encryptionHash: `0x${string}`,
): Promise<void> {
  const computed = (await sha256HexOfBytes(plainBytes)).toLowerCase()
  const expected = normalizeBytes32Hex(encryptionHash).toLowerCase()
  if (computed !== expected) {
    throw new Error(
      'Decryption failed: integrity check failed. The decrypted content does not match the hash stored on-chain.',
    )
  }
}

/**
 * Throws if base64-decoded plaintext bytes do not match on-chain encryptionHash.
 */
export async function assertDecryptedMatchesEncryptionHash(
  plaintextBase64: string,
  encryptionHash: `0x${string}`,
): Promise<void> {
  await assertBytesMatchEncryptionHash(
    base64ToUint8(plaintextBase64),
    encryptionHash,
  )
}
